import React, { useCallback, useMemo } from 'react';

import {
  IonIcon,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
} from '@ionic/react';
import { alertCircleOutline } from 'ionicons/icons';

import Typography from 'components/Typography';
import { useIsIncluded } from 'hooks/useIsIncluded';
import { DateRangeFormatter, useCurrencyFormat, usePercentFormat } from 'intl';
import {
  ACTION_APPROVE,
  ACTION_MORE_INFO,
  ACTION_REJECT,
  ACTION_UNDO,
  IMPORTANCE_COLOR_MAP,
  STATUS_COLOR_MAP,
} from 'scenes/FinancePage/FR/constants';
import { IMPORTANCE_URGENT } from 'scenes/FinancePage/FR/constants';
import { getRoute } from 'scenes/FinancePage/FR/constants';
import {
  useApprove,
  useMoreInfo,
  useReject,
  useUndo,
} from 'scenes/FinancePage/FR/data/mutations';
import Avatar from 'scenes/FinancePage/components/Avatar';
import { AVATAR_BY_ACTION_TYPE_MAP } from 'scenes/FinancePage/constants';
import { IFRData } from 'scenes/FinancePage/interfaces';

interface IItem {
  item: IFRData;
}

const FRItem: React.FC<IItem> = ({ item }) => {
  const [approve] = useApprove();
  const [reject] = useReject();
  const [undo] = useUndo();
  const [moreInfo] = useMoreInfo();

  const {
    id,
    actionType,
    status,
    businessEntities,
    period,
    importance,
    difference,
    spend,
    roasD7,
    isBudgetOverrun = false,
  }: any = useMemo(() => {
    return item?.data;
  }, [item?.data]);

  const percentSignFormat = usePercentFormat({
    maximumFractionDigits: 0,
    signDisplay: 'exceptZero',
  });
  const percentROASFormat = usePercentFormat({
    maximumFractionDigits: 2,
  });
  const formatSpend = useCurrencyFormat({
    currency: spend.currencyCode,
    maximumFractionDigits: 2,
  });
  const detailsLine = useMemo(() => {
    return `Spend: ${formatSpend(spend.amount)} ${
      difference !== 0 ? `(${percentSignFormat(difference)})` : ''
    } / ROAS D7: ${percentROASFormat(roasD7)}`;
  }, [
    formatSpend,
    spend,
    percentSignFormat,
    difference,
    percentROASFormat,
    roasD7,
  ]);

  const isShowUrgent = useMemo(() => {
    return importance?.id === IMPORTANCE_URGENT;
  }, [importance]);

  const isActionAllowed = useIsIncluded(item?.data?.actions);

  const allowedActionUndo = isActionAllowed(ACTION_UNDO);
  const allowedActionMoreInfo = isActionAllowed(ACTION_MORE_INFO);
  const allowedActionApprove = isActionAllowed(ACTION_APPROVE);
  const allowedActionReject = isActionAllowed(ACTION_REJECT);
  const primaryActions = allowedActionUndo || allowedActionMoreInfo;
  const secondaryActions = allowedActionUndo || allowedActionMoreInfo;

  const handleApprove = useCallback(() => {
    approve({ id });
  }, [id, approve]);

  const handleReject = useCallback(() => {
    reject({ id });
  }, [id, reject]);

  const handleUndo = useCallback(() => {
    undo({ id });
  }, [id, undo]);

  const handleMoreInfo = useCallback(() => {
    moreInfo({ id });
  }, [id, moreInfo]);

  const businessEntityName = useMemo(() => {
    return businessEntities?.map((el: any) => el.name).join(' / ');
  }, [businessEntities]);

  return (
    <IonItemSliding>
      {primaryActions ? (
        <IonItemOptions side={'start'}>
          {allowedActionUndo ? (
            <IonItemOption color={'medium'} onClick={handleUndo}>
              UNDO
            </IonItemOption>
          ) : null}
          {allowedActionMoreInfo ? (
            <IonItemOption color={'primary'} onClick={handleMoreInfo}>
              REQUEST MORE INFO
            </IonItemOption>
          ) : null}
        </IonItemOptions>
      ) : null}
      <IonItem
        routerLink={getRoute(id)}
        routerDirection={'forward'}
        button={true}
        detail={false}
      >
        <Avatar
          slot={'start'}
          color={STATUS_COLOR_MAP.get(status?.id)}
          type={AVATAR_BY_ACTION_TYPE_MAP.get(actionType)}
        >
          FR
        </Avatar>
        <div>
          <Typography variant={'overline'} color={'medium'}>
            <DateRangeFormatter value={period} template={'MONTH'} />
            &nbsp;/
            {isShowUrgent ? (
              <>
                &nbsp;
                <Typography
                  color={IMPORTANCE_COLOR_MAP.get(importance?.id)}
                  className={'ion-text-uppercase'}
                  variant={'overline'}
                  inline={true}
                >
                  {importance?.name}
                </Typography>
                &nbsp;/
              </>
            ) : null}{' '}
            &nbsp;#{id}
          </Typography>
          <Typography variant={'subtitle1'} color={'primary'} lineClamp={1}>
            {businessEntityName}
          </Typography>
          <Typography>{detailsLine}</Typography>
        </div>
        {isBudgetOverrun && (
          <IonIcon
            slot={'end'}
            icon={alertCircleOutline}
            color={'danger'}
            size={'medium'}
          />
        )}
      </IonItem>
      {secondaryActions ? (
        <IonItemOptions side={'end'}>
          {allowedActionApprove ? (
            <IonItemOption color={'success'} onClick={handleApprove}>
              APPROVE
            </IonItemOption>
          ) : null}
          {allowedActionReject ? (
            <IonItemOption color={'danger'} onClick={handleReject}>
              REJECT
            </IonItemOption>
          ) : null}
        </IonItemOptions>
      ) : null}
    </IonItemSliding>
  );
};

export default FRItem;
